import dayjs from 'dayjs'
import type { LimitedSpace, Space } from '@/types'
import { StripeTrialStatuses } from '@/types'
import TRIAL_DETAILS from '@constants/trialDays'

import { isSpaceWithoutPlan } from './getSpacePlanType'

/**
 * LEGEND:
 * Starter Plan: Space created without a plan, this is a legacy plan that should be possible to create anymore but we still have occurrences in the database (mostly should be expired by now which means with access blocked on the frontend until they subscribe to a plan)
 * New 14-days Trial: Space created with a subscription to a plan but with an initial 14-days trial period - customer is charged only after the trial period ends
 */

/**
 * It returns if the trial period has expired
 *
 * @param  {Object} space
 * @return {boolean}
 */
export const isExpiredTrial = (space: Space | LimitedSpace): boolean => {
  let expirationDate = dayjs
    .utc(space.created_at)
    .add(TRIAL_DETAILS.PERIOD_IN_DAYS, TRIAL_DETAILS.UNIT)

  if (
    space.stripe_trial_status === StripeTrialStatuses.SUBSCRIBED &&
    space.stripe_trial_end
  ) {
    expirationDate = dayjs(space.stripe_trial_end)
  } else if (isSpaceWithSubscriptionWithoutTrial(space)) {
    expirationDate = dayjs.utc(space.created_at).add(0, TRIAL_DETAILS.UNIT) // as the community without cc doesnt have trial days, the workaround is to set the diff to zero
  }

  return expirationDate.isBefore(dayjs.utc())
}

/**
 * It returns if the space is an (old) starter plan (space with no plan) under a trial
 *
 * @param {Object} space
 * @returns {boolean}
 */
export const isSpaceWithoutPlanUnderTrial = (
  space: Space | LimitedSpace,
): boolean => {
  return (
    isSpaceWithoutPlan(space) &&
    Boolean(space.trial) &&
    !space.stripe_trial_status
  )
}

export const isSpaceWithoutPlanUnderExpiredTrial = (
  space: Space | LimitedSpace,
): boolean => {
  return isSpaceWithoutPlan(space) && isExpiredTrial(space)
}

/**
 * It return if the space is an (old) starter plan (space with no plan) under a trial or a new 14-days trial
 *
 * @param {Object} space
 * @returns {boolean}
 */
export const isTrialSpace = (space: Space | LimitedSpace): boolean => {
  return isSpaceWithoutPlanUnderTrial(space) || isStripeTrial(space)
}

/**
 * It return if the space is an (old) starter plan (space with no plan) with an extended trial
 *
 * @param {Object} space
 * @returns {boolean}
 */
export const isExtendedTrial = (space: Space | LimitedSpace): boolean => {
  return isExpiredTrial(space) && isSpaceWithoutPlanUnderTrial(space)
}

/**
 * It return if the space is a New 14-days Trial
 *
 * @param {Object} space
 * @returns {boolean}
 */
export const isStripeTrial = (space: Space | LimitedSpace): boolean => {
  return (
    space.stripe_trial_status === StripeTrialStatuses.SUBSCRIBED &&
    Boolean(leftDaysOfTrial(space)) // for the community without credit card
  )
}

/**
 * It returns if it is a space with a subscription but without a trial
 *
 * @param {Object} space
 * @returns {boolean}
 */
export const isSpaceWithSubscriptionWithoutTrial = (
  space: Space | LimitedSpace,
): boolean => {
  return (
    space.stripe_trial_status === StripeTrialStatuses.SUBSCRIBED &&
    space.stripe_trial_end === null // for the community without credit card for example
  )
}

/**
 * It returns the remaining days of the trial period
 *
 * @param {Object} space
 * @returns {number | undefined}
 */
export const leftDaysOfTrial = (
  space: Space | LimitedSpace,
): number | undefined => {
  // When a space has no created date or on the community without credit card, it should return undefined.
  // The community without credit card plan does not have a trial period.
  if (
    !space.created_at ||
    ('no_cc_community' in space && space.no_cc_community)
  ) {
    return undefined
  }

  const hasStripeTrialExpirationDate = !!space.stripe_trial_end

  let expirationDate = dayjs
    .utc(space.created_at)
    .add(TRIAL_DETAILS.PERIOD_IN_DAYS, TRIAL_DETAILS.UNIT)

  if (hasStripeTrialExpirationDate) {
    expirationDate = dayjs.utc(space.stripe_trial_end)
  }

  if (!expirationDate?.isValid()) {
    return undefined
  }

  const now = dayjs().utc()
  const remainingDays = expirationDate.diff(now, TRIAL_DETAILS.UNIT) + 1
  return remainingDays <= 0 ? 0 : remainingDays
}
